import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import FallbackImg from "../../../components/FallbackImg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import ErrorIcon from "../../../assets/aftersalesservice_icons/error_code_icon.svg";
import SearchImg from "../../../assets/aftersalesservice_icons/search.png";
import Caution from "../../../assets/aftersalesservice_icons/caution.svg";
import TickBlue from "../../../assets/aftersalesservice_icons/tick-blue.svg";
import Info from "../../../assets/aftersalesservice_icons/info.svg";
import troubleshootEn from "./troubleshoot_errors_en.json";
import troubleshootCh from "./troubleshoot_errors_ch.json";
import troubleshootMy from "./troubleshoot_errors_my.json";
import { useLanguage } from "../LanguageContext";
import { buildAfterSalesQuery } from "../serialTypeUtils";

const TROUBLESHOOT_DATA = {
  en: troubleshootEn,
  ch: troubleshootCh,
  my: troubleshootMy,
};

const ErrorCode = () => {
  const { trans, lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;

  const data = TROUBLESHOOT_DATA[lang] ?? troubleshootEn;
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState(data);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setFiltered(data);
  }, [data]);

  useEffect(() => {
    const q = query.toLowerCase().trim();
    if (q === "") {
      setFiltered(data);
      return;
    }

    const filteredData = data.filter((error) => {
      const indicator = (error.error_indicator || "").toLowerCase();
      const errorText = (error.error || "").toLowerCase();
      const solutionMatch = Array.isArray(error.possible_causes_and_solution)
        ? error.possible_causes_and_solution.some((s) =>
            (s || "").toLowerCase().includes(q),
          )
        : false;

      return indicator.includes(q) || errorText.includes(q) || solutionMatch;
    });

    setFiltered(filteredData);
  }, [query, data]);

  // helper to open detail modal
  const showErrorDetails = (error) => {
    setSelected(error);
  };

  const closeModal = () => setSelected(null);

  // render
  return (
    <AfterSalesLayout>
      <div className="as-error-code-content" id="error_code_modal">
        <div style={{ padding: "0 31.97px" }}>
          <div className="as-error-code-container">
            <button
              type="button"
              className="as-back-btn"
              id="error_code_back"
              onClick={() => window.history.back()}
            >
              <FallbackImg src={BackBlue} alt="back_blue_icon" />
              {trans.back}
            </button>

            <div className="as-error-code-main">
              <FallbackImg src={ErrorIcon} alt="error_code_icon" />
              <div>
                <div className="as-error-code-main-title">
                  {trans.error_code}
                </div>
                <div className="as-error-code-main-text">
                  {trans.error_code_sub}
                </div>
              </div>
            </div>

            <div className="as-error-code-searchbar">
              <FallbackImg src={SearchImg} alt="search" id="search" />
              <input
                placeholder="Search error code..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className="as-error-code-count">
              {filtered.length}{" "}
              {filtered.length === 1
                ? trans.error_code_singular
                : trans.error_code_plural}
            </div>
          </div>

          <div className="as-error-code-container">
            <div
              className="as-ewarranty-troubleshoot-list"
              id="ewarranty-troubleshoot-list"
              style={{ display: filtered.length ? "grid" : "none" }}
            >
              {filtered.map((error, idx) => (
                <div
                  key={idx}
                  className={`as-ewarranty-troubleshoot-box${selected === error ? " as-active" : ""}`}
                  onClick={() => showErrorDetails(error)}
                >
                  {error.error_indicator}
                </div>
              ))}
            </div>

            <div
              className="as-no-results"
              id="no-results"
              style={{ display: filtered.length ? "none" : "block" }}
            >
              {trans.no_error}
            </div>
          </div>

          <div className="as-contact-support-container">
            <div className="as-contact-support-text">{trans.cant_find}</div>
            <div className="as-contact-support-text">{trans.contact_our}</div>
            <Link
              to={`/aftersalesservice/ticketsubmission${buildAfterSalesQuery(
                searchParams,
                {
                  type: "Technical_Consultation_Inquiry",
                },
              )}`}
            >
              <button
                type="button"
                className="as-contact-support-btn"
                id="error-contact-support-btn"
              >
                {trans.contact_sup}
              </button>
            </Link>
          </div>
        </div>

        <div id="modal-container">
          {selected && (
            <div className="as-troubleshoot-error-modal">
              <div className="as-troubleshoot-error-content">
                <div className="as-troubleshoot-error-header">
                  <div className="as-troubleshoot-error-code-container">
                    <div className="as-troubleshoot-error-code">
                      {selected.error_indicator}
                    </div>
                    <div className="as-troubleshoot-error-title">
                      {trans.error_code}
                      <br />
                      {trans.error_details}
                    </div>
                  </div>
                  <button
                    className="as-close-troubleshoot"
                    id="close-troubleshoot"
                    onClick={closeModal}
                  >
                    &times;
                  </button>
                </div>

                <div className="as-troubleshoot-error-main">
                  <div className="as-troubleshoot-error-item as-red">
                    <FallbackImg src={Caution} alt="caution" id="caution" />
                    <div className="as-troubleshoot-error-subitem">
                      <div className="as-troubleshoot-error-title as-red">
                        {trans.error}
                      </div>
                      <div className="as-troubleshoot-error-text">
                        {selected.error}
                      </div>
                    </div>
                  </div>

                  {selected.possible_causes_and_solution &&
                    selected.possible_causes_and_solution.length > 0 && (
                      <div className="as-troubleshoot-error-item as-blue">
                        <FallbackImg
                          src={TickBlue}
                          alt="tick-blue"
                          id="tick-blue"
                        />
                        <div className="as-troubleshoot-error-subitem">
                          <div className="as-troubleshoot-error-title as-blue">
                            {trans.possible_causes}
                          </div>
                          {selected.possible_causes_and_solution.map((s, i) => (
                            <div className="as-troubleshoot-error-text" key={i}>
                              {s}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {selected.remarks && (
                    <div className="as-troubleshoot-error-item as-yellow">
                      <FallbackImg src={Info} alt="info" id="info" />
                      <div className="as-troubleshoot-error-subitem">
                        <div className="as-troubleshoot-error-title as-yellow">
                          {trans.remarks}
                        </div>
                        <div className="as-troubleshoot-error-text">
                          {selected.remarks}
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <button
                      type="button"
                      className="as-back-troubleshoot"
                      id="back-troubleshoot"
                      onClick={closeModal}
                    >
                      {trans.back_error}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default ErrorCode;
